import React, { useEffect, useState } from "react";
import { adminApi } from "./adminApi";
import { toast } from "sonner";

const FIELDS = [
    { key: "founding_label", label: "Offer label", type: "input" },
    { key: "founding_price", label: "Price", type: "input" },
    { key: "founding_caption", label: "Caption (small, under price)", type: "input" },
    { key: "founding_blurb", label: "Founding blurb (long)", type: "textarea" },
];

const DISCOUNT_FIELDS = [
    { key: "discount_banner", label: "Top banner copy", type: "input" },
    { key: "discount_blurb", label: "Discount blurb (long)", type: "textarea" },
];

export default function AdminPricing() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const p = await adminApi.pricing();
            setCfg(p);
        })();
    }, []);

    const setField = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const data = await adminApi.updatePricing(cfg);
            setCfg(data);
            toast.success("Pricing updated");
        } catch {
            toast.error("Could not save");
        } finally {
            setSaving(false);
        }
    };

    if (!cfg) return <div className="p-10 text-ink-muted">Loading…</div>;

    return (
        <div className="p-10 max-w-3xl" data-testid="admin-pricing">
            <span className="text-[10px] tracking-[0.36em] uppercase text-gold-deep">Commerce</span>
            <h1 className="mt-2 font-display text-5xl text-burgundy">
                <span className="italic">Founding</span> offer
            </h1>
            <p className="mt-2 text-sm text-ink-muted max-w-md">
                Single-tier "Founding 100" model. Edits go live immediately on the public paywall.
            </p>

            <div className="mt-8 grid grid-cols-1 gap-6">
                <Section title="Default paywall" subtitle="Shown the first time a user reaches the paywall.">
                    {FIELDS.map(f => (
                        <Row key={f.key} label={f.label}>
                            {f.type === "textarea" ? (
                                <textarea
                                    data-testid={`field-${f.key}`}
                                    value={cfg[f.key] || ""}
                                    onChange={e => setField(f.key, e.target.value)}
                                    rows={4}
                                    className="w-full bg-cream border border-burgundy/20 focus:border-burgundy p-3 font-display text-base text-ink focus:outline-none"
                                />
                            ) : (
                                <input
                                    data-testid={`field-${f.key}`}
                                    value={cfg[f.key] || ""}
                                    onChange={e => setField(f.key, e.target.value)}
                                    className="w-full bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-lg text-burgundy focus:outline-none"
                                />
                            )}
                        </Row>
                    ))}
                </Section>

                <Section title="Retry paywall (after dismissal)" subtitle="Shown when the user dismisses the first paywall.">
                    <label className="flex items-center gap-2 mb-4">
                        <input
                            data-testid="field-discount_active"
                            type="checkbox"
                            checked={!!cfg.discount_active}
                            onChange={e => setField("discount_active", e.target.checked)}
                            className="w-4 h-4 accent-burgundy"
                        />
                        <span className="text-xs tracking-[0.24em] uppercase text-ink">
                            Show retry paywall
                        </span>
                    </label>
                    {DISCOUNT_FIELDS.map(f => (
                        <Row key={f.key} label={f.label}>
                            {f.type === "textarea" ? (
                                <textarea
                                    data-testid={`field-${f.key}`}
                                    value={cfg[f.key] || ""}
                                    onChange={e => setField(f.key, e.target.value)}
                                    rows={3}
                                    className="w-full bg-cream border border-burgundy/20 focus:border-burgundy p-3 font-display text-base text-ink focus:outline-none"
                                />
                            ) : (
                                <input
                                    data-testid={`field-${f.key}`}
                                    value={cfg[f.key] || ""}
                                    onChange={e => setField(f.key, e.target.value)}
                                    className="w-full bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-lg text-burgundy focus:outline-none"
                                />
                            )}
                        </Row>
                    ))}
                </Section>
            </div>

            <div className="mt-10">
                <button
                    data-testid="pricing-save-btn"
                    onClick={save}
                    disabled={saving}
                    className="bg-burgundy text-cream uppercase text-xs tracking-[0.22em] py-3 px-8 hover:bg-burgundy-deep transition-colors disabled:opacity-60"
                >
                    {saving ? "Saving…" : "Save pricing"}
                </button>
            </div>
        </div>
    );
}

function Section({ title, subtitle, children }) {
    return (
        <div className="border border-burgundy/15 bg-cream p-6">
            <div className="font-display text-2xl text-burgundy">{title}</div>
            <div className="text-xs text-ink-muted mb-5">{subtitle}</div>
            <div className="space-y-4">{children}</div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-ink-muted mb-1">{label}</div>
            {children}
        </div>
    );
}
